import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, Send, AlertCircle,
  BarChart3, Settings, LogOut, Sun, Plus, Menu, X, Sparkles, Building2, Briefcase, UserPlus, Rocket, Boxes, Inbox, ShieldCheck
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import TenantSwitcher from "@/components/TenantSwitcher";
import OnboardingProgressBadge from "@/components/OnboardingProgressBadge";
import BillingStatusBanner from "@/components/BillingStatusBanner";

// Agentur-Ebene (nur agency_admin/platform_admin)
const AGENCY_NAV = [
  { to: "/agency/dashboard", icon: Briefcase, label: "Übersicht" },
  { to: "/agency/clients", icon: Building2, label: "Mandanten" },
  { to: "/agency/onboarding", icon: UserPlus, label: "Onboarding" },
  { to: "/admin/agencies", icon: ShieldCheck, label: "Platform-Admin", onlyForRoles: ["platform_admin"] },
];

// Mandanten-Ebene (für alle Rollen)
const TENANT_NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/leads", icon: Users, label: "Leads" },
  { to: "/queue", icon: Inbox, label: "Conversion Queue" },
  { to: "/offers", icon: FileText, label: "Angebote" },
  { to: "/followups", icon: Send, label: "Nachfassung" },
  { to: "/objections", icon: AlertCircle, label: "Einwände" },
  { to: "/reporting", icon: BarChart3, label: "Reporting" },
  { to: "/configurator", icon: Boxes, label: "Konfigurator", hideForRoles: ["client_user"] },
  { to: "/settings", icon: Settings, label: "Mandanten-Einstellungen" },
];

export default function Layout() {
  const { user, company, agency, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const isAgencyAdmin = user?.role === "agency_admin" || user?.role === "platform_admin";
  const inAgencyView = loc.pathname.startsWith("/agency") || loc.pathname.startsWith("/admin");
  const canEditClientOnboarding = user?.role !== "client_user";
  const needsClientOnboarding = !!company && company.onboarding_completed === false;

  // Section-Color je nach Kontext
  const sectionAccent = inAgencyView ? "#7c3aed" : "#ea580c"; // purple für Agentur, orange für Mandant

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar — auf Desktop fixed in voller Bildschirmhöhe */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed inset-y-0 left-0 z-40 w-72 h-screen bg-white border-r border-slate-200 flex flex-col transition-transform duration-200`}>
        {/* Brand */}
        <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-orange-500 flex items-center justify-center">
            <Sun className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>revenioo.ai</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">PV Sales Suite</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {/* AGENTUR-Sektion */}
          {isAgencyAdmin && (
            <div className="px-3 pb-3" data-testid="nav-section-agency">
              <SectionHeader
                color="#7c3aed"
                label="Agentur"
                value={agency?.name || "—"}
                active={inAgencyView}
                icon={Briefcase}
              />
              <div className="mt-2 space-y-0.5">
                {AGENCY_NAV.filter((n) => !n.onlyForRoles || n.onlyForRoles.includes(user?.role)).map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`nav-agency-${label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-violet-600 text-white shadow-sm"
                          : "text-slate-600 hover:bg-violet-50 hover:text-violet-700"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          {isAgencyAdmin && <div className="mx-5 my-2 border-t border-slate-100" />}

          {/* MANDANT-Sektion */}
          <div className="px-3 pt-2" data-testid="nav-section-tenant">
            <SectionHeader
              color="#ea580c"
              label="Mandant"
              value={company?.name || "Kein Mandant"}
              active={!inAgencyView}
              icon={Building2}
            />
            {/* TenantSwitcher integriert hier */}
            {isAgencyAdmin && (
              <div className="mt-2">
                <TenantSwitcher />
              </div>
            )}
            <div className="mt-2 space-y-0.5">
              {needsClientOnboarding && canEditClientOnboarding && (
                <NavLink
                  to="/onboarding/client"
                  data-testid="nav-tenant-client-onboarding"
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                      isActive
                        ? "bg-orange-600 text-white border-orange-600 shadow-sm"
                        : "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
                    }`
                  }
                >
                  <Rocket className="w-4 h-4" />
                  <span className="flex-1">Angebotsauftritt einrichten</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold opacity-80">Setup</span>
                </NavLink>
              )}
              {TENANT_NAV.filter(({ hideForRoles }) => !(hideForRoles && user?.role && hideForRoles.includes(user.role))).map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`nav-tenant-${label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-slate-900 text-white shadow-sm"
                        : "text-slate-600 hover:bg-orange-50 hover:text-orange-700"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        {/* Quick-Action-Buttons nur in Mandanten-View sinnvoll */}
        {!inAgencyView && (
          <div className="px-3 pb-3 space-y-2 border-t border-slate-100 pt-3">
            <Button
              data-testid="new-lead-ai-cta"
              onClick={() => nav("/leads/new-ai")}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-9"
            >
              <Sparkles className="w-4 h-4 mr-2" /> KI-Chat
            </Button>
            <Button
              data-testid="new-lead-cta"
              onClick={() => nav("/leads/new")}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-9"
            >
              <Plus className="w-4 h-4 mr-2" /> Neuer Lead
            </Button>
          </div>
        )}

        {/* User */}
        <div className="border-t border-slate-200 p-4 space-y-2">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold">
                {user?.name?.[0] || "?"}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button data-testid="sidebar-toggle" onClick={() => setOpen(!open)} className="text-slate-700">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <span className="font-bold">revenioo.ai</span>
        <div className="w-5" />
      </div>

      {/* Main — auf Desktop links um Sidebar-Breite versetzt */}
      <main className="flex-1 mt-14 md:mt-0 md:ml-72">
        {/* Context-Badge (oben sticky) — zeigt klar an wo man ist */}
        <ContextBadge inAgencyView={inAgencyView} agency={agency} company={company} accent={sectionAccent} isAgencyAdmin={isAgencyAdmin} />
        <BillingStatusBanner />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// ===== Helpers =====
const SectionHeader = ({ color, label, value, active, icon: Icon }) => (
  <div
    className="flex items-center gap-2 px-2 py-1.5 rounded-md"
    style={active ? { background: `${color}14` } : {}}
  >
    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
    <div className="min-w-0 flex-1">
      <p className="text-[9px] uppercase tracking-[0.15em] font-bold" style={{ color }}>{label}</p>
      <p className="text-xs font-semibold text-slate-900 truncate leading-tight">{value}</p>
    </div>
    {active && <Icon className="w-3 h-3 shrink-0" style={{ color }} />}
  </div>
);

const ContextBadge = ({ inAgencyView, agency, company, accent, isAgencyAdmin }) => (
  <div
    data-testid="context-badge"
    className="sticky top-0 z-20 backdrop-blur-md bg-white/85 border-b"
    style={{ borderBottomColor: `${accent}33` }}
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8 h-10 flex items-center gap-3 text-xs">
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-bold uppercase tracking-widest text-[10px]"
        style={{ background: `${accent}15`, color: accent }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
        {inAgencyView ? "Agentur-Ansicht" : "Mandanten-Ansicht"}
      </span>
      <span className="text-slate-400">·</span>
      <span className="text-slate-700 font-medium truncate">
        {inAgencyView ? (agency?.name || "Ihre Agentur") : (company?.name || "—")}
      </span>
      {/* Quick cross-toggle (nur für Agency-Admins sinnvoll) */}
      {isAgencyAdmin && (
        <div className="ml-auto flex items-center gap-2">
          <OnboardingProgressBadge />
          <NavLink
            data-testid="context-toggle"
            to={inAgencyView ? "/dashboard" : "/agency/dashboard"}
            className="text-[11px] font-medium hover:underline"
            style={{ color: inAgencyView ? "#ea580c" : "#7c3aed" }}
          >
            {inAgencyView ? "→ Zu Mandant wechseln" : "→ Zur Agentur wechseln"}
          </NavLink>
        </div>
      )}
    </div>
  </div>
);
